/**
 * FinancialConvo.ts - Fiinancial Conversation Model
 * Contains information from theFinancial Conversation Request created by a user.
 *
 * NOTE: declaredTypeOfAssets field contains potentially duplicate data that is currently in the
 * Estate table. We are storing this info here as both a snapshot in time and to protect estate
 * information from being edited by potentially angry beneficiaries
 */
import { financialConvoApi } from "../apis";

// database field names
export type FinancialConvoJSON = {
  id?: number;
  has_completed_request?: boolean;
  financial_feelings?: string;
  financial_experience?: string;
  discussion_topics?: string; // json string to be converted to string[]
  discussion_topics_something_else?: string;
  declared_type_of_assets?: string; // json string to be converted to string[]
  bene_relationship_status?: string;
  anything_else_to_know?: string;
  opportunity_id?: number;
};

export class FinancialConvo {
  static path = "/financial-convos";
  path = "/financial-convos";

  constructor(
    public id?: number,
    public hasCompletedRequest?: boolean,
    public financialFeelings?: string,
    public financialExperience?: string,
    public discussionTopics?: string[],
    public discussionTopicsSomethingElse?: string,
    public declaredTypeOfAssets?: string[],
    public beneRelationshipStatus?: string,
    public anythingElseToKnow?: string,
    public opportunityId?: number
  ) {}

  // clone
  static from(financialConvo: FinancialConvo): FinancialConvo {
    return new FinancialConvo(
      financialConvo.id,
      financialConvo.hasCompletedRequest,
      financialConvo.financialFeelings,
      financialConvo.financialExperience,
      financialConvo.discussionTopics,
      financialConvo.discussionTopicsSomethingElse,
      financialConvo.declaredTypeOfAssets,
      financialConvo.beneRelationshipStatus,
      financialConvo.anythingElseToKnow,
      financialConvo.opportunityId
    );
  }

  static fromJSON = (json: FinancialConvoJSON): FinancialConvo => {
    return new FinancialConvo(
      json.id,
      json.has_completed_request,
      json.financial_feelings,
      json.financial_experience,
      json.discussion_topics ? JSON.parse(json.discussion_topics) : undefined,
      json.discussion_topics_something_else,
      json.declared_type_of_assets
        ? JSON.parse(json.declared_type_of_assets)
        : undefined,
      json.bene_relationship_status,
      json.anything_else_to_know,
      json.opportunity_id
    );
  };

  toJson(): FinancialConvoJSON {
    const json: any = {};
    this.hasCompletedRequest !== undefined &&
      (json.has_completed_request = this.hasCompletedRequest);
    this.financialFeelings !== undefined &&
      (json.financial_feelings = this.financialFeelings);
    this.financialExperience !== undefined &&
      (json.financial_experience = this.financialExperience);
    this.discussionTopics !== undefined &&
      (json.discussion_topics = JSON.stringify(this.discussionTopics));
    this.discussionTopicsSomethingElse !== undefined &&
      (json.discussion_topics_something_else =
        this.discussionTopicsSomethingElse);
    this.declaredTypeOfAssets !== undefined &&
      (json.declared_type_of_assets = JSON.stringify(
        this.declaredTypeOfAssets
      ));
    this.beneRelationshipStatus !== undefined &&
      (json.bene_relationship_status = this.beneRelationshipStatus);
    this.anythingElseToKnow !== undefined &&
      (json.anything_else_to_know = this.anythingElseToKnow);
    this.opportunityId !== undefined &&
      (json.opportunity_id = this.opportunityId);
    return json;
  }

  async saveRequest(): Promise<FinancialConvo> {
    if (this?.id) {
      const fields = this.toJson();
      await financialConvoApi.updateRequest(this.id, fields);
    } else {
      this.id = await financialConvoApi.createRequest(this.toJson());
    }
    return this;
  }
  async submitRequest(): Promise<FinancialConvo> {
    if (this?.id) {
      return await financialConvoApi.submitRequest(this.id);
    } else {
      throw "Financial Convo Request does not have an ID";
    }
  }
  async checkRequestSubmit(): Promise<boolean> {
    if (this?.id) {
      return await financialConvoApi.checkRequestSubmit(this.id);
    } else {
      throw "Financial Convo Request does not have an ID";
    }
  }
}
